import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { 
  Settings, 
  Palette, 
  Type, 
  Code, 
  FileText,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Save,
  Eye,
  Globe,
  Image,
  Layout,
  RefreshCw,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function AdminCustomization() {
  const [activeTab, setActiveTab] = useState('design');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Site settings state
  const [settings, setSettings] = useState({
    primary_color: '#9B1B30',
    secondary_color: '#1A1A1A',
    background_color: '#FFFFFF',
    text_color: '#333333',
    hero_title: '',
    hero_subtitle: '',
    hero_button_text: '',
    about_title: '',
    about_text: '',
    footer_text: '',
    footer_address: '',
    footer_phone: '',
    footer_email: '',
    custom_css: '',
    meta_title: '',
    meta_description: '',
    custom_scripts: ''
  });
  
  // Custom pages state
  const [pages, setPages] = useState([]);
  const [showPageDialog, setShowPageDialog] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [pageForm, setPageForm] = useState({
    title: '',
    slug: '',
    content: '',
    is_published: true,
    show_in_menu: false,
    menu_order: 0
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchPages();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/site-customization');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPages = async () => {
    try {
      const response = await api.get('/admin/custom-pages');
      setPages(response.data || []);
    } catch (error) {
      console.error('Error fetching pages:', error);
    }
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      await api.put('/admin/site-customization', settings);
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const openCreatePageDialog = () => {
    setEditingPage(null);
    setPageForm({
      title: '',
      slug: '',
      content: '',
      is_published: true,
      show_in_menu: false,
      menu_order: 0
    });
    setShowPageDialog(true);
  };

  const openEditPageDialog = (page) => {
    setEditingPage(page);
    setPageForm({
      title: page.title,
      slug: page.slug,
      content: page.content,
      is_published: page.is_published,
      show_in_menu: page.show_in_menu,
      menu_order: page.menu_order || 0
    });
    setShowPageDialog(true);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[äöüß]/g, match => ({ 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss' }[match]))
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleTitleChange = (title) => {
    setPageForm(prev => ({
      ...prev,
      title,
      slug: editingPage ? prev.slug : generateSlug(title)
    }));
  };

  const savePage = async () => {
    if (!pageForm.title || !pageForm.slug) {
      toast.error('Titel und URL sind erforderlich');
      return;
    }
    
    setIsSaving(true);
    try {
      if (editingPage) {
        await api.put(`/admin/custom-pages/${editingPage.id}`, pageForm);
        toast.success('Seite aktualisiert');
      } else {
        await api.post('/admin/custom-pages', pageForm);
        toast.success('Seite erstellt');
      }
      setShowPageDialog(false);
      fetchPages();
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDeletePage = (page) => {
    setPageToDelete(page);
    setShowDeleteDialog(true);
  };

  const deletePage = async () => {
    if (!pageToDelete) return;
    
    setIsSaving(true);
    try {
      await api.delete(`/admin/custom-pages/${pageToDelete.id}`);
      toast.success('Seite gelöscht');
      setShowDeleteDialog(false);
      setPageToDelete(null);
      fetchPages();
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error('Fehler beim Löschen');
    } finally {
      setIsSaving(false);
    }
  };

  const previewCss = () => {
    // Create a style element and inject custom CSS
    let styleEl = document.getElementById('custom-preview-css');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'custom-preview-css';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = settings.custom_css;
    toast.success('CSS-Vorschau aktiviert');
  };

  const resetCssPreview = () => {
    const styleEl = document.getElementById('custom-preview-css');
    if (styleEl) {
      styleEl.remove();
    }
    toast.info('CSS-Vorschau zurückgesetzt');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6 md:w-7 md:h-7 text-[#9B1B30]" />
            Website-Anpassungen
          </h1>
          <p className="text-sm text-gray-600 mt-1">Design, Texte und Seiten anpassen</p>
        </div>
        <Button 
          onClick={saveSettings}
          disabled={isSaving}
          className="bg-[#9B1B30] hover:bg-[#7a1526]"
        >
          {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Alle Änderungen speichern
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-5 max-w-3xl">
          <TabsTrigger value="design" className="text-xs sm:text-sm">
            <Palette className="w-4 h-4 mr-1 sm:mr-2" />
            Design
          </TabsTrigger>
          <TabsTrigger value="content" className="text-xs sm:text-sm">
            <Type className="w-4 h-4 mr-1 sm:mr-2" />
            Texte
          </TabsTrigger>
          <TabsTrigger value="pages" className="text-xs sm:text-sm">
            <FileText className="w-4 h-4 mr-1 sm:mr-2" />
            Seiten
          </TabsTrigger>
          <TabsTrigger value="css" className="text-xs sm:text-sm">
            <Code className="w-4 h-4 mr-1 sm:mr-2" />
            CSS
          </TabsTrigger>
          <TabsTrigger value="seo" className="text-xs sm:text-sm">
            <Globe className="w-4 h-4 mr-1 sm:mr-2" />
            SEO
          </TabsTrigger>
        </TabsList>

        {/* Design Tab */}
        <TabsContent value="design" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-[#9B1B30]" />
                Farben
              </CardTitle>
              <CardDescription>Passen Sie die Hauptfarben Ihrer Website an</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Primärfarbe</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings.primary_color}
                      onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label>Sekundärfarbe</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={settings.secondary_color}
                      onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings.secondary_color}
                      onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label>Hintergrund</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={settings.background_color}
                      onChange={(e) => setSettings({...settings, background_color: e.target.value})}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings.background_color}
                      onChange={(e) => setSettings({...settings, background_color: e.target.value})}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label>Textfarbe</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={settings.text_color}
                      onChange={(e) => setSettings({...settings, text_color: e.target.value})}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings.text_color}
                      onChange={(e) => setSettings({...settings, text_color: e.target.value})}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
              </div>
              
              {/* Color Preview */}
              <div className="mt-6 p-6 rounded-lg border" style={{ backgroundColor: settings.background_color }}>
                <h3 className="text-lg font-bold mb-2" style={{ color: settings.primary_color }}>Vorschau Überschrift</h3>
                <p style={{ color: settings.text_color }}>Dies ist ein Beispieltext in der gewählten Textfarbe.</p>
                <button 
                  className="mt-4 px-4 py-2 rounded text-white"
                  style={{ backgroundColor: settings.primary_color }}
                >
                  Beispiel Button
                </button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Content Tab */}
        <TabsContent value="content" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layout className="w-5 h-5 text-[#9B1B30]" />
                Hero-Bereich
              </CardTitle>
              <CardDescription>Der große Bereich oben auf der Startseite</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Hauptüberschrift</Label>
                <Input
                  value={settings.hero_title}
                  onChange={(e) => setSettings({...settings, hero_title: e.target.value})}
                  placeholder="z.B. Willkommen bei FarbWelt"
                />
              </div>
              <div>
                <Label>Untertitel</Label>
                <Textarea
                  value={settings.hero_subtitle}
                  onChange={(e) => setSettings({...settings, hero_subtitle: e.target.value})}
                  placeholder="z.B. Ihr Partner für professionelle Malerarbeiten"
                  rows={2}
                />
              </div>
              <div>
                <Label>Button-Text</Label>
                <Input
                  value={settings.hero_button_text}
                  onChange={(e) => setSettings({...settings, hero_button_text: e.target.value})}
                  placeholder="z.B. Jetzt Termin buchen"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#9B1B30]" />
                Über uns
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Titel</Label>
                <Input
                  value={settings.about_title}
                  onChange={(e) => setSettings({...settings, about_title: e.target.value})}
                  placeholder="z.B. Über FarbWelt"
                />
              </div>
              <div>
                <Label>Text</Label>
                <Textarea
                  value={settings.about_text}
                  onChange={(e) => setSettings({...settings, about_text: e.target.value})}
                  placeholder="Beschreiben Sie Ihr Unternehmen..."
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#9B1B30]" />
                Footer
              </CardTitle>
              <CardDescription>Der Fußbereich der Website</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Footer-Text</Label>
                <Input
                  value={settings.footer_text}
                  onChange={(e) => setSettings({...settings, footer_text: e.target.value})}
                  placeholder="z.B. © 2026 FarbWelt. Alle Rechte vorbehalten."
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Adresse</Label>
                  <Input
                    value={settings.footer_address}
                    onChange={(e) => setSettings({...settings, footer_address: e.target.value})}
                    placeholder="Straße, PLZ Ort"
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input
                    value={settings.footer_phone}
                    onChange={(e) => setSettings({...settings, footer_phone: e.target.value})}
                    placeholder="0123 456789"
                  />
                </div>
                <div>
                  <Label>E-Mail</Label>
                  <Input
                    value={settings.footer_email}
                    onChange={(e) => setSettings({...settings, footer_email: e.target.value})}
                    placeholder="info@beispiel.de"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pages Tab */}
        <TabsContent value="pages" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#9B1B30]" />
                  Eigene Seiten
                </CardTitle>
                <CardDescription>Erstellen Sie eigene Inhaltsseiten</CardDescription>
              </div>
              <Button onClick={openCreatePageDialog} className="bg-[#9B1B30] hover:bg-[#7a1526]">
                <Plus className="w-4 h-4 mr-2" />
                Neue Seite
              </Button>
            </CardHeader>
            <CardContent>
              {pages.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Noch keine eigenen Seiten erstellt</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pages.map(page => (
                    <div 
                      key={page.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{page.title}</p>
                          {page.is_published ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Veröffentlicht</span>
                          ) : (
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Entwurf</span>
                          )}
                          {page.show_in_menu && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Im Menü</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">/{page.slug}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => window.open(`/seite/${page.slug}`, '_blank')}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openEditPageDialog(page)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => confirmDeletePage(page)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CSS Tab */}
        <TabsContent value="css" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="w-5 h-5 text-[#9B1B30]" />
                Eigenes CSS
              </CardTitle>
              <CardDescription>Fügen Sie eigene CSS-Regeln hinzu, um das Design anzupassen</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={settings.custom_css}
                onChange={(e) => setSettings({...settings, custom_css: e.target.value})}
                placeholder={`/* Beispiel CSS */
.mein-element {
  color: red;
  font-size: 16px;
}

/* Überschriften anpassen */
h1 {
  font-weight: bold;
}`}
                className="font-mono text-sm min-h-[300px]"
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={previewCss}>
                  <Eye className="w-4 h-4 mr-2" />
                  Vorschau
                </Button>
                <Button variant="outline" onClick={resetCssPreview}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Vorschau zurücksetzen
                </Button>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg text-sm">
                <p className="font-medium text-yellow-800 mb-2">💡 CSS-Tipps:</p>
                <ul className="list-disc list-inside text-yellow-700 space-y-1">
                  <li>Klassen mit Punkt: <code>.meine-klasse</code></li>
                  <li>IDs mit Raute: <code>#meine-id</code></li>
                  <li>Primärfarbe nutzen: <code>color: var(--primary);</code></li>
                  <li>Wichtig machen: <code>!important</code></li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="w-5 h-5 text-[#9B1B30]" />
                Eigene Scripts
              </CardTitle>
              <CardDescription>JavaScript-Code (wird im Footer eingefügt)</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={settings.custom_scripts}
                onChange={(e) => setSettings({...settings, custom_scripts: e.target.value})}
                placeholder={`<!-- Google Analytics, etc. -->
<script>
  console.log('Custom script loaded');
</script>`}
                className="font-mono text-sm min-h-[150px]"
              />
              <p className="text-xs text-gray-500 mt-2">⚠️ Nur verwenden, wenn Sie wissen, was Sie tun.</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#9B1B30]" />
                SEO-Einstellungen
              </CardTitle>
              <CardDescription>Suchmaschinenoptimierung für bessere Sichtbarkeit</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Seitentitel (Meta Title)</Label>
                <Input
                  value={settings.meta_title}
                  onChange={(e) => setSettings({...settings, meta_title: e.target.value})}
                  placeholder="z.B. FarbWelt - Professionelle Malerarbeiten in Dormagen"
                />
                <p className="text-xs text-gray-500 mt-1">Erscheint im Browser-Tab und in Google-Suchergebnissen</p>
              </div>
              <div>
                <Label>Beschreibung (Meta Description)</Label>
                <Textarea
                  value={settings.meta_description}
                  onChange={(e) => setSettings({...settings, meta_description: e.target.value})}
                  placeholder="z.B. FarbWelt bietet hochwertige Malerarbeiten, Fassadengestaltung und Renovierungen in Dormagen und Umgebung."
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">Erscheint als Beschreibung in Google-Suchergebnissen (max. 160 Zeichen empfohlen)</p>
              </div>
              
              {/* SEO Preview */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">Google-Vorschau:</p>
                <div className="border rounded p-3 bg-white">
                  <p className="text-blue-600 text-lg hover:underline cursor-pointer">
                    {settings.meta_title || 'Seitentitel'}
                  </p>
                  <p className="text-green-700 text-sm">www.farbwelt-service.de</p>
                  <p className="text-gray-600 text-sm">
                    {settings.meta_description || 'Beschreibung Ihrer Website...'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Page Create/Edit Dialog */}
      <Dialog open={showPageDialog} onOpenChange={setShowPageDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingPage ? <Pencil className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingPage ? 'Seite bearbeiten' : 'Neue Seite erstellen'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Titel *</Label>
              <Input
                value={pageForm.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="z.B. Impressum"
              />
            </div>
            <div>
              <Label>URL (Slug) *</Label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">/seite/</span>
                <Input
                  value={pageForm.slug}
                  onChange={(e) => setPageForm({...pageForm, slug: generateSlug(e.target.value)})}
                  placeholder="impressum"
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <Label>Inhalt</Label>
              <Textarea
                value={pageForm.content}
                onChange={(e) => setPageForm({...pageForm, content: e.target.value})}
                placeholder="Seiteninhalt (HTML erlaubt)..."
                className="min-h-[200px]"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="published"
                  checked={pageForm.is_published}
                  onCheckedChange={(checked) => setPageForm({...pageForm, is_published: checked})}
                />
                <Label htmlFor="published">Veröffentlicht</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="menu"
                  checked={pageForm.show_in_menu}
                  onCheckedChange={(checked) => setPageForm({...pageForm, show_in_menu: checked})}
                />
                <Label htmlFor="menu">Im Menü anzeigen</Label>
              </div>
            </div>
            {pageForm.show_in_menu && (
              <div>
                <Label>Menü-Reihenfolge</Label>
                <Input
                  type="number"
                  value={pageForm.menu_order}
                  onChange={(e) => setPageForm({...pageForm, menu_order: parseInt(e.target.value) || 0})}
                  min={0}
                />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowPageDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={savePage}
                disabled={isSaving}
                className="bg-[#9B1B30] hover:bg-[#7a1526]"
              >
                {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Seite löschen?
            </DialogTitle>
            <DialogDescription className="pt-2">
              Möchten Sie die Seite <strong>"{pageToDelete?.title}"</strong> wirklich löschen?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              variant="destructive"
              onClick={deletePage}
              disabled={isSaving}
            >
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Löschen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
